"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GradingFormSchema, gradingSchema } from "@/features/grading/gradingSchema";
import { useGrade, useGradingTable } from "@/features/grading/useGrade";
import { useURLSync } from "@/features/grading/useURLSync";
import { zodResolver } from "@hookform/resolvers/zod";
import { sendGTMEvent } from "@next/third-parties/google";
import { CheckIcon, ChevronDownIcon, HelpCircleIcon, LinkIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useStore } from "zustand";
import { Skeleton } from "../ui/skeleton";
import { useSearchParams } from "next/navigation";
import { Label } from "../ui/label";
import GradingChart from "@/features/grading/grading-chart";

export default function GradeSettingsForm() {
  const params = useSearchParams()
  const { store, data, _hasHydrated, highlightTableRow } = useStore(useGrade, (store) => store);
  const { possibleScores, maxScore, passingGrade } = useGradingTable();
  const { copyShareLink } = useURLSync();
  const [copied, setCopied] = useState(false);
  const [quickScore, setQuickScore] = useState("");
  const [quickGrade, setQuickGrade] = useState("");
  const [isQuickLookupOpen, setIsQuickLookupOpen] = useState(false);
  const [isChartOpen, setIsChartOpen] = useState(false);
  const scoreDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const gradeDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Score -> Grade calculation
  const quickScoreResult = useMemo(() => {
    if (quickScore === "") return null;
    const score = parseFloat(quickScore);
    if (isNaN(score) || score < 0 || score > maxScore) return null;

    const match = possibleScores.find((s) => s.score === score);
    if (match) return match.grade;

    const closest = possibleScores.reduce((prev, curr) =>
      Math.abs(curr.score - score) < Math.abs(prev.score - score) ? curr : prev
    );
    return closest?.grade ?? null;
  }, [quickScore, possibleScores, maxScore]);

  const isQuickScorePassing = quickScoreResult !== null && quickScoreResult >= Number(passingGrade);

  // Grade -> Score calculation
  const quickGradeResult = useMemo(() => {
    if (quickGrade === "") return null;
    const targetGrade = parseFloat(quickGrade);
    if (isNaN(targetGrade) || targetGrade < 1 || targetGrade > 10) return null;

    const matching = possibleScores.filter((s) => s.grade >= targetGrade);
    if (matching.length === 0) return null;

    const minScore = matching.reduce((prev, curr) =>
      curr.score < prev.score ? curr : prev
    );
    return minScore.score;
  }, [quickGrade, possibleScores]);

  // Handler: Score → Grade (scrolls and highlights row with debounce)
  const handleQuickScoreChange = (value: string) => {
    setQuickScore(value);

    // Clear previous debounce
    if (scoreDebounceRef.current) {
      clearTimeout(scoreDebounceRef.current);
    }

    // Debounce the scroll/highlight to allow typing multi-digit numbers
    scoreDebounceRef.current = setTimeout(() => {
      const score = parseFloat(value);
      if (!isNaN(score) && score >= 0 && score <= maxScore) {
        const match = possibleScores.find((s) => s.score === score);
        if (match) {
          highlightTableRow(score);
        }
      }
    }, 500);
  };

  // Handler: Grade → Score (scrolls and highlights row with debounce)
  const handleQuickGradeChange = (value: string) => {
    setQuickGrade(value);

    // Clear previous debounce
    if (gradeDebounceRef.current) {
      clearTimeout(gradeDebounceRef.current);
    }

    // Debounce the scroll/highlight to allow typing multi-digit numbers
    gradeDebounceRef.current = setTimeout(() => {
      const targetGrade = parseFloat(value);
      if (!isNaN(targetGrade) && targetGrade >= 1 && targetGrade <= 10) {
        const matching = possibleScores.filter((s) => s.grade >= targetGrade);
        if (matching.length > 0) {
          const minScore = matching.reduce((prev, curr) =>
            curr.score < prev.score ? curr : prev
          );
          highlightTableRow(minScore.score);
        }
      }
    }, 500);
  };

  const handleCopyLink = async () => {
    const success = await copyShareLink();
    if (success) {
      setCopied(true);
      sendGTMEvent({ event: "shareLinkCopied" });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const defaultValues: GradingFormSchema = {
    passingGrade: params.get("passingGrade") as GradingFormSchema["passingGrade"] || data.passingGrade,
    maxScore: params.get("maxScore") || data.maxScore,
    term: params.get("term") || data.term,
    linearPercentage: params.get("linearPercentage") || data.linearPercentage,
    pointsType: params.get("pointsType") as GradingFormSchema["pointsType"] || data.pointsType,
  }
  const form = useForm({
    resolver: zodResolver(gradingSchema),
    defaultValues: defaultValues,
    reValidateMode: "onChange",
  });

  const previousValuesRef = useRef(form.getValues());

  useEffect(() => {
    const subscription = form.watch((values) => {
      if (
        JSON.stringify(values) !== JSON.stringify(previousValuesRef.current)
      ) {
        store({
          passingGrade: values.passingGrade ?? "5.5",
          maxScore: values.maxScore ?? "",
          term: values.term ?? "100",
          linearPercentage: values.linearPercentage ?? "50",
          pointsType: values.pointsType ?? "full",
        });
        previousValuesRef.current = {
          passingGrade: values.passingGrade ?? "5.5",
          maxScore: values.maxScore ?? "",
          term: values.term ?? "100",
          linearPercentage: values.linearPercentage ?? "50",
          pointsType: values.pointsType ?? "full",
        };
      }
    });
    return () => subscription.unsubscribe();
  }, [form, store]);

  if (!_hasHydrated) {
    return <Skeleton className="w-full h-113.25" />;
  }

  return (
    <Card className="overflow-hidden pt-0 pb-0">
      <CardContent className="p-6 md:p-8">
        <Form {...form}>
          <form
            id="grade-settings-form"
            name="grade-settings-form"
            className="space-y-5 md:space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit(() => { })(e);
            }}
          >
            {/* Section heading */}
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Toetsinstellingen
            </p>

            {/* Primary settings in a grid */}
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              <FormField
                control={form.control}
                name="maxScore"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium flex items-center gap-1.5">
                      Max. Score
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircleIcon className="h-3.5 w-3.5 text-muted-foreground/70" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              De maximale score die behaald kan worden op de
                              toets.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          sendGTMEvent({ event: "maxScoreChanged" });
                        }}
                        type="number"
                        placeholder="30"
                        className="h-11 md:h-12 text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="linearPercentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium flex items-center gap-1.5">
                      Cesuur %
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircleIcon className="h-3.5 w-3.5 text-muted-foreground/70" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              Het percentage van de maximale score dat nodig is
                              voor een voldoende.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          sendGTMEvent({ event: "linearPercentageChanged" });
                        }}
                        type="number"
                        placeholder="50"
                        className="h-11 md:h-12 text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-5">
              <FormField
                control={form.control}
                name="passingGrade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Voldoende
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircleIcon className="h-3.5 w-3.5 text-muted-foreground/70" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              Het cijfer dat als voldoende geldt. Standaard 5.5.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        sendGTMEvent({ event: "passingGradeChanged", value });
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-11 md:h-12 text-base w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {gradingSchema.shape.passingGrade.options.map(
                          (value) => (
                            <SelectItem key={value} value={value}>
                              {value}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="term"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium flex items-center gap-1.5">
                      N-Term
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircleIcon className="h-3.5 w-3.5 text-muted-foreground/70" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              De N-term voor normalisatie van examens. Standaard
                              1.0 voor reguliere toetsen.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        sendGTMEvent({ event: "termChanged", value });
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-11 md:h-12 text-base w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Array.from({ length: 21 }, (_, i) => i * 10).map(
                          (value) => (
                            <SelectItem key={value} value={String(value)}>
                              {(value / 100).toFixed(1)}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="pointsType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Puntenverdeling
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircleIcon className="h-3.5 w-3.5 text-muted-foreground/70" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">
                            Kies of de toets hele, halve, kwart- of tiendepunten
                            gebruikt.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      sendGTMEvent({ event: "pointsTypeChanged", value });
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11 md:h-12 text-base w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="full">Hele punten (1, 2, 3...)</SelectItem>
                      <SelectItem value="half">Halve punten (0.5, 1, 1.5...)</SelectItem>
                      <SelectItem value="quarter">Kwart punten (0.25, 0.5...)</SelectItem>
                      <SelectItem value="tenth">Tiende punten (0.1, 0.2...)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Share button */}
            <Button
              type="button"
              variant="outline"
              onClick={handleCopyLink}
              className="w-full"
            >
              {copied ? (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Link gekopieerd!
                </>
              ) : (
                <>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Deel deze instellingen
                </>
              )}
            </Button>

            <Collapsible open={isQuickLookupOpen} onOpenChange={setIsQuickLookupOpen}>
              <CollapsibleTrigger className="flex w-full items-center justify-between py-2 hover:opacity-80 transition-opacity">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Snel opzoeken
                </p>
                <ChevronDownIcon
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isQuickLookupOpen ? "rotate-180" : ""
                    }`}
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                <div className="space-y-4 pt-4">
                  {/* Score → Grade Input */}
                  <div className="space-y-2">
                    <Label htmlFor="quick-score" className="text-sm font-medium">
                      Score → Cijfer

                      {quickScoreResult !== null && (
                        <p className="text-sm ml-auto">
                          Cijfer:{" "}
                          <span
                            className={
                              isQuickScorePassing
                                ? "text-success font-semibold"
                                : "text-warning font-semibold"
                            }
                          >
                            {quickScoreResult.toFixed(1)}
                          </span>
                        </p>
                      )}
                    </Label>
                    <Input
                      id="quick-score"
                      type="number"
                      placeholder="Voer score in..."
                      value={quickScore}
                      onChange={(e) => handleQuickScoreChange(e.target.value)}
                      className="h-10"
                    />
                  </div>

                  {/* Grade → Score Input */}
                  <div className="space-y-2">
                    <Label htmlFor="quick-grade" className="text-sm font-medium">
                      Cijfer → Score
                       {quickGradeResult !== null && (
                      <p className="text-sm ml-auto">
                        Minimale score: <span className="font-semibold">{quickGradeResult}</span>
                      </p>
                    )}
                    </Label>
                    <Input
                      id="quick-grade"
                      type="number"
                      step="0.1"
                      placeholder="Voer cijfer in..."
                      value={quickGrade}
                      onChange={(e) => handleQuickGradeChange(e.target.value)}
                      className="h-10"
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
            <Collapsible open={isChartOpen} onOpenChange={setIsChartOpen}>
              <CollapsibleTrigger className="flex w-full items-center justify-between py-2 hover:opacity-80 transition-opacity">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Grafiek
                </p>
                <ChevronDownIcon
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isChartOpen ? "rotate-180" : ""
                    }`}
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                <div className="mt-4">
                  <GradingChart showCard={false} />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
